import type { ApiAuthType } from '@/app/api/auth/[...everything]+api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { H2 } from '@/components/ui/typography';
import { generateAPIUrl } from '@/utils/api-url';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { router } from 'expo-router';
import Head from 'expo-router/head';
import { fetch as expoFetch } from 'expo/fetch';
import { hc } from 'hono/client';
import { Fragment, useCallback, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const LOGO = require('../../assets/images/icon-dark.png');

const client = hc<ApiAuthType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

async function submitAuth(email: string) {
  const res = await client.api.auth.$post({ json: { email } });

  if (!res.ok) {
    if (res.status === 429) {
      throw new Error(await res.text());
    }

    throw new Error(`Failed to login, please try again. ${res.statusText}`);
  }

  return res.json();
}

export default function LoginScreen() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const submitAuthCallback = useCallback(() => {
    if (!isValidEmail(email)) {
      alert('Please enter a valid email address');
      return;
    }

    setLoading(true);
    submitAuth(email)
      .then((data) => {
        if (data?.otpId) {
          return Promise.all([
            AsyncStorage.setItem('otp', data.otpId),
            AsyncStorage.setItem('email', email)
          ]);
        }
        throw new Error('Failed to login, please try again');
      })
      .then(() => {
        setLoading(false);
        router.push('/auth/verify');
      })
      .catch((err) => {
        if (err instanceof Error) {
          alert(err.message);
        } else {
          alert('An unexpected error occurred, please try again later.');
        }
        setLoading(false);
      });
  }, [email]);

  return (
    <Fragment>
      <Head>
        <title>Sign in or create your account - Lexikon AI</title>
      </Head>
      <SafeAreaView className="flex-1 web:w-full web:self-center web:lg:w-[50%]">
        <View className="flex-1 self-stretch web:pt-12">
          <ScrollView contentInsetAdjustmentBehavior="always">
            <View className="flex-1 px-12">
              <View className="native:pt-8 items-center pb-1">
                <Image source={LOGO} style={styles.logo} resizeMode="contain" />
                <H2 className="border-0 pb-1 pt-4 text-center">
                  Sign in or create your account
                </H2>
                <Text className="pt-4 text-center">
                  Enter your email to resume where you left off or get started
                </Text>
              </View>
              <View className="pt-4">
                <Input
                  placeholder="you@email.com"
                  onSubmitEditing={submitAuthCallback}
                  submitBehavior="submit"
                  autoFocus
                  value={email}
                  onChangeText={setEmail}
                  inputMode="email"
                  keyboardType="email-address"
                  textContentType="emailAddress"
                  returnKeyType="next"
                />
              </View>
              {Platform.OS === 'web' ? (
                <View className="self-stretch py-4">
                  <SubmitButton
                    loading={loading}
                    onPress={submitAuthCallback}
                  />
                </View>
              ) : null}
            </View>
          </ScrollView>
        </View>
        {Platform.OS !== 'web' ? (
          <View className="self-stretch px-8 pb-4">
            <SubmitButton loading={loading} onPress={submitAuthCallback} />
          </View>
        ) : null}
        <KeyboardAvoidingView behavior="padding" />
      </SafeAreaView>
    </Fragment>
  );
}

function SubmitButton({
  loading,
  onPress
}: {
  loading: boolean;
  onPress: () => void;
}) {
  return (
    <Button
      size="lg"
      className="rounded-full"
      onPress={onPress}
      disabled={loading}
    >
      {loading ? (
        <View className="py-1">
          <ActivityIndicator color="black" />
        </View>
      ) : (
        <Text>Continue</Text>
      )}
    </Button>
  );
}

const styles = StyleSheet.create({
  logo: {
    height: 100
  }
});
